import { useEffect, useRef } from 'react';
import { useRouter } from 'core/i18n/navigation';

import { onIdTokenChanged } from 'firebase/auth';
import getFirebaseAuth from 'integrations/firebase/firebase';
import { closeLoginModal, ModalActor } from 'xstate/actors/ModalActor';
import {
  LoginModalPayload,
  ModalView,
  StateValue,
} from 'xstate/machines/ModalMachine';
import { appRoutes } from 'config';
import { cookieKeys, getCookieStore } from 'core/storage/cookies';
import { AuthActor, requestFirebaseAuthChanged } from 'xstate/actors/AuthActor';
import { EmittedEventType } from 'xstate/machines/AuthMachine';

// App Router counterpart to useAuthenticationObserver. Same Firebase→XState
// reconciliation and auth-hint cookie mirroring, but uses next/navigation's
// router (next/router is unavailable in the App Router). The "redirect off the
// login page after sign-in" check reads window.location so we don't re-subscribe
// the observer on every navigation.
const useAuthenticationObserverApp = (): void => {
  const router = useRouter();
  // Firebase calls us again every time it refreshes the token, about once an
  // hour. Remember who was signed in last time we were called, so the things
  // that should only happen when someone actually signs in (closing the
  // login modal, redirecting into the app) don't run again on each refresh.
  const lastUserId = useRef<string | null>(null);

  useEffect(() => {
    // Listen to Firebase for sign-in, sign-out, the session being restored
    // on page load, and token refreshes. (onIdTokenChanged covers all of
    // these; onAuthStateChanged would miss the refreshes, and we want the
    // auth machine to always hold the token Firebase currently has.)
    const unregisterAuthObserver = onIdTokenChanged(
      getFirebaseAuth(),
      (user) => {
        // Reconcile the persisted XState AuthMachine with the true Firebase
        // session (see SP-555).
        requestFirebaseAuthChanged(user);

        // A different user than last time means someone just signed in.
        // The same user again is just a token refresh.
        const isNewSignIn = user !== null && user.uid !== lastUserId.current;
        lastUserId.current = user?.uid ?? null;

        // Mirror the Firebase session into the non-authoritative hint cookie the
        // edge middleware reads to bump authenticated users off public pages.
        if (user) {
          getCookieStore().setItem(cookieKeys.authHint, true, {
            maxAgeSeconds: 60 * 60 * 24 * 30,
            sameSite: 'Lax',
            secure: process.env.NODE_ENV === 'production',
          });
        } else {
          getCookieStore().removeItem(cookieKeys.authHint);
        }

        if (user && isNewSignIn) {
          // Signing in from the login modal should land in the app just like
          // signing in on /login does — read its target before closing it.
          const modal = ModalActor.getSnapshot();
          const isLoginModalOpen =
            modal.matches(StateValue.Open) &&
            modal.context.view === ModalView.Login;
          const loginModalRedirect = isLoginModalOpen
            ? (modal.context.payload as LoginModalPayload | undefined)
                ?.redirectTo
            : undefined;

          closeLoginModal();

          const returnTo = getCookieStore().getItem(cookieKeys.returnTo);
          if (returnTo) {
            getCookieStore().removeItem(cookieKeys.returnTo);
            router.push(returnTo);
          } else if (
            isLoginModalOpen ||
            (typeof window !== 'undefined' &&
              window.location.pathname.endsWith(appRoutes.LOGIN))
          ) {
            router.push(loginModalRedirect ?? appRoutes.MEMORY_SPACE);
          }
        }
      },
    );
    return () => {
      unregisterAuthObserver();
    };
  }, [router]);

  // Send the user home when the auth machine finishes an explicit logout. The
  // machine emits LOGGED_OUT instead of navigating itself, so routing stays in
  // React.
  useEffect(() => {
    const subscription = AuthActor.on(EmittedEventType.LoggedOut, async () => {
      // router.replace(appRoutes.HOME);
      if (typeof window !== 'undefined') {
        window.location.assign('/');
      }
    });
    return () => subscription.unsubscribe();
  }, [router]);
};

export default useAuthenticationObserverApp;
